import { useEffect, useState } from "react";
import {
  clearCharacters,
  getCharacter,
  listCharacters,
  removeCharacter,
  saveCharacter,
} from "@/features/characters/storage/repository";
import {
  createCharacterFromInput,
  updateCharacterFromInput,
} from "@/features/characters/character-utils";
import type { Character, CharacterInput } from "@/features/characters/types";
import type { ActiveStorageEngine } from "@/features/settings/storage/preferences";

export function useCharacters() {
  const [items, setItems] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [engine, setEngine] = useState<ActiveStorageEngine>("indexeddb");

  async function refresh() {
    try {
      setLoading(true);
      setError(null);
      const snapshot = await listCharacters();
      setItems(snapshot.items);
      setEngine(snapshot.engine);
    } catch {
      setError("Não foi possível carregar as fichas locais.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function createCharacter(input: CharacterInput) {
    const character = createCharacterFromInput(input);
    const activeEngine = await saveCharacter(character);
    setEngine(activeEngine);
    await refresh();
    return character;
  }

  async function updateCharacter(id: string, input: CharacterInput) {
    const existing = items.find((item) => item.id === id);

    if (!existing) {
      const loaded = await getCharacter(id);
      if (!loaded.item) {
        throw new Error("Personagem não encontrado");
      }

      const updatedFallback = updateCharacterFromInput(loaded.item, input);
      const activeEngine = await saveCharacter(updatedFallback);
      setEngine(activeEngine);
      await refresh();
      return updatedFallback;
    }

    const updated = updateCharacterFromInput(existing, input);
    const activeEngine = await saveCharacter(updated);
    setEngine(activeEngine);
    await refresh();
    return updated;
  }

  async function deleteCharacter(id: string) {
    const activeEngine = await removeCharacter(id);
    setEngine(activeEngine);
    await refresh();
  }

  async function wipeCharacters() {
    const activeEngine = await clearCharacters();
    setEngine(activeEngine);
    await refresh();
  }

  return {
    items,
    loading,
    error,
    engine,
    refresh,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    wipeCharacters,
  };
}
